<template>
  <div v-if="visible" class="modal-overlay">
    <div class="modal-contenido">
      <h4 class="modal-titulo">{{ esEdicion ? 'Editar Área' : 'Nueva Área' }}</h4>

      <!-- Área superior / Facultad -->
      <div class="form-group" v-if="opcionesPlanas.length">
        <label>{{ nivel ? 'Facultad' : 'Área superior jerárquica' }}</label>
        <select v-model="padreSeleccionado" class="input-select">
          <option :value="null">— Ninguna —</option>
          <option
            v-for="op in opcionesPlanas"
            :key="op.id"
            :value="op.id"
          >{{ op.label }}</option>
        </select>
      </div>

      <!-- Nombre del área -->
      <div class="form-group">
        <label>Nombre del área</label>
        <input
          v-model="nombre"
          type="text"
          placeholder="Ej. Facultad de Ingeniería"
          class="input-campo"
          @keyup.enter="confirmar"
        />
        <span v-if="errores.nombre" class="campo-error">{{ errores.nombre }}</span>
      </div>

      <!-- Tipo de área -->
      <div class="form-group">
        <label>Tipo de área</label>
        <input
          v-model="tipo"
          type="text"
          placeholder="ej: Facultad, Programa, Administrativa, Investigación"
          class="input-campo"
        />
      </div>

      <!-- Nivel — siempre visible, obligatorio si tipo === 'programa' -->
      <div class="form-group">
        <label>Nivel <span class="nota-campo">(solo aplica para programas)</span></label>
        <div class="radio-grupo">
          <label class="radio-opcion">
            <input type="radio" v-model="nivel" value="pregrado" /> Pregrado
          </label>
          <label class="radio-opcion">
            <input type="radio" v-model="nivel" value="posgrado" /> Posgrado
          </label>
        </div>
        <span v-if="errores.nivel" class="campo-error">{{ errores.nivel }}</span>
      </div>

      <!-- Campus (multi-selección) -->
      <div class="form-group" v-if="opcionesCampus && opcionesCampus.length">
        <label>Campus asignados</label>

        <!-- Chips de campus ya seleccionados -->
        <div class="campus-chips" v-if="campusSeleccionados.length">
          <span
            v-for="c in campusSeleccionados"
            :key="c.id_campus"
            class="campus-chip"
          >
            {{ c.nombre }}
            <button type="button" class="chip-quitar" @click="quitarCampus(c.id_campus)">×</button>
          </span>
        </div>
        <p v-else class="campus-vacio">Sin campus asignado</p>

        <!-- Select para agregar campus -->
        <div class="campus-agregar" v-if="campusDisponibles.length">
          <select v-model="campusParaAgregar" class="input-select campus-select">
            <option :value="null">— Agregar campus —</option>
            <option v-for="c in campusDisponibles" :key="c.id_campus" :value="c.id_campus">
              {{ c.nombre }}
            </option>
          </select>
          <button type="button" class="btn btn-agregar" @click="agregarCampus" :disabled="campusParaAgregar === null">
            Agregar
          </button>
        </div>
      </div>

      <div v-if="errorGlobal" class="error-global">{{ errorGlobal }}</div>

      <div class="botones">
        <button class="btn salir" @click.stop="$emit('cancelar')">Cancelar</button>
        <button class="btn guardar" :disabled="guardando" @click.stop="confirmar">
          {{ guardando ? 'Guardando...' : (esEdicion ? 'Guardar cambios' : 'Crear área') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Alta y edición de un área.
 *
 * Cambiar el padre MUEVE el área con toda su descendencia; el backend rechaza el
 * movimiento si formaría un ciclo.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, watch, computed } from 'vue'
import type { Programa } from '@/interfaces/Programa'
import type { Campus } from '@/interfaces/Campus'

const props = defineProps<{
  visible: boolean
  area?: Programa | null
  opcionesPlanas: { id: number; label: string; nombre?: string }[]
  opcionesCampus?: Campus[]
}>()

const emit = defineEmits<{
  (e: 'cancelar'): void
  (e: 'guardar', payload: {
    nombre: string
    tipo?: string
    nivel?: 'pregrado' | 'posgrado' | null
    padre_id: number | null
    facultad?: string | null
    campus_ids: number[]
  }): void
}>()

const nombre            = ref('')
const tipo              = ref('')
const nivel             = ref<'pregrado' | 'posgrado' | null>(null)
const padreSeleccionado = ref<number | null>(null)
const errores           = ref<Record<string, string>>({})
const errorGlobal       = ref('')
const guardando         = ref(false)

// Lista local de campus asignados en el modal
const campusSeleccionados = ref<{ id_campus: number; nombre: string }[]>([])
const campusParaAgregar   = ref<number | null>(null)

const esEdicion = computed(() => !!props.area?.id_programa)

// Campus que aún no están en la lista seleccionada
const campusDisponibles = computed(() =>
  (props.opcionesCampus ?? []).filter(
    c => !campusSeleccionados.value.some(s => s.id_campus === c.id_campus)
  )
)

watch(
  () => props.visible,
  (v) => {
    if (v) {
      nombre.value            = props.area?.nombre   ?? ''
      tipo.value              = props.area?.tipo     ?? ''
      nivel.value             = props.area?.nivel    ?? null
      padreSeleccionado.value = props.area?.padre_id ?? null
      // Pre-cargar campus actuales del área en modo edición
      campusSeleccionados.value = (props.area?.campus ?? []).map(c => ({
        id_campus: c.id_campus,
        nombre:    c.nombre
      }))
      campusParaAgregar.value = null
      errores.value           = {}
      errorGlobal.value       = ''
      guardando.value         = false
    }
  }
)

function agregarCampus() {
  if (campusParaAgregar.value === null) return
  const campus = props.opcionesCampus?.find(c => c.id_campus === campusParaAgregar.value)
  if (!campus) return
  campusSeleccionados.value.push({ id_campus: campus.id_campus, nombre: campus.nombre })
  campusParaAgregar.value = null
}

function quitarCampus(idCampus: number) {
  campusSeleccionados.value = campusSeleccionados.value.filter(c => c.id_campus !== idCampus)
}

function confirmar() {
  errores.value = {}
  errorGlobal.value = ''
  if (!nombre.value.trim()) {
    errores.value.nombre = 'El nombre es obligatorio.'
    return
  }
  if (tipo.value.toLowerCase().trim() === 'programa' && !nivel.value) {
    errores.value.nivel = 'El nivel es obligatorio para áreas de tipo Programa.'
    return
  }

  const esPrograma = tipo.value.toLowerCase().trim() === 'programa'
  const facultad = esPrograma && nivel.value
    ? (props.opcionesPlanas.find(o => o.id === padreSeleccionado.value)?.nombre ?? null)
    : null

  emit('guardar', {
    nombre:     nombre.value.trim(),
    tipo:       tipo.value.trim() || undefined,
    nivel:      esPrograma ? (nivel.value || null) : null,
    padre_id:   padreSeleccionado.value,
    facultad:   facultad || null,
    campus_ids: campusSeleccionados.value.map(c => c.id_campus)
  })
}

defineExpose({
  setGuardando: (v: boolean) => { guardando.value = v },
  setError: (msg: string) => { errorGlobal.value = msg }
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.48);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.modal-contenido {
  background: white;
  padding: 1.75rem;
  border-radius: 12px;
  width: 480px;
  max-width: 96vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  font-family: var(--font-family);
}

.modal-titulo {
  margin: 0 0 1.25rem 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1a;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  font-size: var(--font-size-sm, 0.875rem);
  color: #333;
}

.input-campo,
.input-select {
  padding: 0.75rem;
  border: 1px solid #e6e5e5;
  border-radius: var(--radius-md, 6px);
  font-size: var(--font-size-base, 1rem);
  font-family: var(--font-family);
  background-color: #f9f9f9;
  box-sizing: border-box;
  color: #1a1a1a;
  width: 100%;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.input-campo {
  height: 40px;
}

.input-select {
  height: auto;
  min-height: 42px;
  line-height: 1.4;
  padding-right: 2rem;
  appearance: auto;
}

.input-campo:focus,
.input-select:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.campo-error {
  font-size: 0.8rem;
  color: #d41a1a;
}

.error-global {
  background: #ffeaea;
  border-left: 3px solid #d41a1a;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  color: #b00;
  margin-bottom: 1rem;
}

.botones {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.25rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md, 6px);
  cursor: pointer;
  height: 40px;
  font-size: var(--font-size-sm, 0.875rem);
  font-family: var(--font-family);
  transition: all 0.15s ease;
}

.btn.salir {
  background-color: white;
  border: 1px solid #ccc;
  color: #333;
}

.btn.salir:hover {
  background-color: #d41a1a;
  color: white;
}

.btn.guardar {
  background-color: #eea222;
  color: black;
}

.btn.guardar:hover {
  background-color: #0e69a0;
  color: white;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.nota-campo {
  font-size: 0.78rem;
  color: #888;
  font-weight: 400;
  margin-left: 4px;
}

.radio-grupo {
  display: flex;
  gap: 1.5rem;
  margin-top: 4px;
}

.radio-opcion {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-sm, 0.875rem);
  color: #333;
  cursor: pointer;
  font-weight: 400;
}

.radio-opcion input[type="radio"] {
  accent-color: #0e69a0;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

/* Campus multi-selección */
.campus-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
}

.campus-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #e8f4fd;
  color: #0e69a0;
  border: 1px solid #b3d9f5;
  border-radius: 16px;
  padding: 3px 10px 3px 12px;
  font-size: 0.82rem;
  font-weight: 600;
}

.chip-quitar {
  background: none;
  border: none;
  color: #0e69a0;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  margin-left: 2px;
  display: flex;
  align-items: center;
}

.chip-quitar:hover { color: #d41a1a; }

.campus-vacio {
  font-size: 0.82rem;
  color: #999;
  font-style: italic;
  margin: 0 0 0.5rem 0;
}

.campus-agregar {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.campus-select {
  flex: 1;
}

.btn-agregar {
  padding: 0.4rem 0.9rem;
  font-size: 0.85rem;
  background-color: #eea222;
  color: black;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  white-space: nowrap;
  transition: background-color 0.15s ease;
}

.btn-agregar:hover:not(:disabled) { background-color: #0e69a0; color: white; }
.btn-agregar:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
